import { useState, useCallback } from 'react';
import { Gauge } from 'lucide-react';
import {
  ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { analyticsApi } from '@/api/analytics.api';
import { usePermission } from '@/hooks/usePermission';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/constants/permissions';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';
import { ReportToolbar, RunReportButton } from '@/components/reports/ReportToolbar';
import { LocationSelect } from '@/components/reports/LocationSelect';
import { CostCentreSelect } from '@/components/reports/CostCentreSelect';
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview';
import { ReportHeader } from '@/components/master/report/ReportHeader';
import { ReportFooter } from '@/components/master/report/ReportFooter';
import { exportToExcel } from '@/utils/reportExport';
import type { ExcelColumn } from '@/utils/reportExport';
import {
  INPUT_CLS, LABEL_CLS, TH_CLS, TH_R_CLS, CARD_CLS,
  today, firstOfMonth,
  fmtDate, fmtByFormat,
  StatusPill, StatusDot, UnavailableMetrics, Section, EmptyState,
  CHART_COLOURS,
} from '@/components/analytics/AnalyticsKit';
import type {
  FinancialScorecardDto,
  FinancialScorecardFilter,
  ScorecardCategory,
  ScorecardKpi,
} from '@/types/analytics.types';

// ── Chart helpers ──────────────────────────────────────────────────────────────

function axisAmt(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return v.toFixed(0);
}

const AXIS_TICK = { fontSize: 11 };
const GRID_CLS = 'stroke-gray-200 dark:stroke-gray-700';

interface RadarPoint {
  category: string;
  score: number;
}

/** Only categories the backend actually scored belong on the radar — a null score is not a zero. */
function buildRadarData(categories: ScorecardCategory[]): RadarPoint[] {
  const points: RadarPoint[] = [];
  for (const c of categories) {
    if (c.score !== null) {
      points.push({ category: c.category, score: c.score });
    }
  }
  return points;
}

function scoreTone(score: number | null): string {
  if (score === null) return 'text-gray-400 dark:text-gray-600';
  if (score >= 70) return 'text-green-600 dark:text-green-400';
  if (score >= 40) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

// ── Excel export ───────────────────────────────────────────────────────────────

const EXCEL_COLUMNS: ExcelColumn[] = [
  { key: 'Category', header: 'Category', width: 22 },
  { key: 'Category Score', header: 'Category Score', width: 14 },
  { key: 'KPI', header: 'KPI', width: 32 },
  { key: 'Value', header: 'Value', width: 18 },
  { key: 'Previous', header: 'Previous', width: 18 },
  { key: 'Status', header: 'Status', width: 12 },
  { key: 'Interpretation', header: 'Interpretation', width: 52 },
];

function buildExcelRows(data: FinancialScorecardDto): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];

  for (const c of data.categories) {
    for (const k of c.kpis) {
      const unavailable = k.value === null || k.status === 'NO_DATA';
      rows.push({
        Category: c.category,
        'Category Score': c.score === null ? 'Unavailable' : c.score,
        KPI: k.label,
        // An unavailable KPI must never be exported as 0.
        Value: unavailable ? 'Unavailable' : fmtByFormat(k.value, k.format),
        Previous: k.previousValue === null ? '—' : fmtByFormat(k.previousValue, k.format),
        Status: k.status,
        Interpretation: k.interpretation,
      });
    }
  }

  for (const m of data.unavailableMetrics) {
    rows.push({
      Category: 'Not available',
      'Category Score': '',
      KPI: m,
      Value: 'Unavailable',
      Previous: '',
      Status: 'NO_DATA',
      Interpretation: 'Not computable from the data available in this system.',
    });
  }

  return rows;
}

// ── KPI table ──────────────────────────────────────────────────────────────────

const ScorecardKpiTable = ({ kpis }: { kpis: ScorecardKpi[] }) => {
  if (kpis.length === 0) {
    return <EmptyState message="No KPIs in this category." />;
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm border-collapse min-w-[640px]">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th className={TH_CLS}>KPI</th>
            <th className={TH_R_CLS}>Value</th>
            <th className={cn(TH_CLS, 'w-20 text-center')}>Status</th>
            <th className={TH_CLS}>Interpretation</th>
          </tr>
        </thead>
        <tbody>
          {kpis.map(k => {
            const unavailable = k.value === null || k.status === 'NO_DATA';
            return (
              <tr
                key={k.key}
                className={cn(
                  'border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50',
                  unavailable && 'opacity-70',
                )}
              >
                <td
                  className={cn(
                    'px-3 py-2 text-sm',
                    unavailable
                      ? 'text-gray-400 dark:text-gray-600'
                      : 'text-gray-900 dark:text-gray-100',
                  )}
                >
                  {k.label}
                </td>
                <td
                  className={cn(
                    'px-3 py-2 text-sm text-right tabular-nums font-medium',
                    unavailable
                      ? 'text-gray-400 dark:text-gray-600'
                      : 'text-gray-900 dark:text-gray-100',
                  )}
                >
                  {/* Never render an unmeasured KPI as 0 — it reads as a real zero. */}
                  {unavailable ? '—' : fmtByFormat(k.value, k.format)}
                </td>
                <td className="px-3 py-2 text-center">
                  <StatusDot status={k.status} />
                </td>
                <td
                  className={cn(
                    'px-3 py-2 text-xs',
                    unavailable
                      ? 'text-gray-400 dark:text-gray-600'
                      : 'text-gray-600 dark:text-gray-400',
                  )}
                >
                  {k.interpretation}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────────

export const FinancialScorecardPage = () => {
  const { hasPermission } = usePermission();
  const { user } = useAuth();

  const [filter, setFilter] = useState<FinancialScorecardFilter>({
    fromDate: firstOfMonth(),
    toDate: today(),
  });
  const [data, setData] = useState<FinancialScorecardDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPrint, setShowPrint] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsApi.getScorecard(filter);
      setData(res.data?.data ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  if (!hasPermission(Permissions.RPT_SCORECARD)) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        You do not have permission to view this report.
      </div>
    );
  }

  const handleExcel = () => {
    if (!data) return;
    exportToExcel({
      sheetName: 'Financial Scorecard',
      columns: EXCEL_COLUMNS,
      rows: buildExcelRows(data),
      title: 'Financial Health Scorecard',
      subtitle: `${data.companyName} · ${filter.fromDate} to ${filter.toDate}`,
    }, `FinancialScorecard_${filter.fromDate}_${filter.toDate}`);
  };

  const handlePrint = () => setShowPrint(true);

  const radarData = data ? buildRadarData(data.categories) : [];

  return (
    <div className="flex flex-col h-full gap-4 p-4 overflow-auto">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Gauge size={22} className="text-brand-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Financial Health Scorecard</h1>
        </div>
        <ReportToolbar
          onExcelExport={handleExcel}
          onPrint={handlePrint}
          hasData={!!data}
        />
      </div>

      {/* Filter panel */}
      <div className={cn(CARD_CLS, 'p-4 space-y-3 shrink-0')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={LABEL_CLS}>From Date *</label>
            <input
              type="date"
              value={filter.fromDate}
              onChange={e => setFilter(f => ({ ...f, fromDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>To Date *</label>
            <input
              type="date"
              value={filter.toDate}
              onChange={e => setFilter(f => ({ ...f, toDate: e.target.value }))}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Location</label>
            <LocationSelect
              value={filter.locationId}
              onChange={id => setFilter(f => ({ ...f, locationId: id || undefined }))}
            />
          </div>
          <div>
            <label className={LABEL_CLS}>Cost Centre</label>
            <CostCentreSelect
              value={filter.costCenterId}
              onChange={id => setFilter(f => ({ ...f, costCenterId: id || undefined }))}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <RunReportButton
            onClick={() => void run()}
            isLoading={loading}
            disabled={!filter.fromDate || !filter.toDate}
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty */}
      {!data && !loading && (
        <EmptyState message='Set the date range and click "Run Report" to generate the Financial Health Scorecard.' />
      )}

      {/* Results */}
      {data && (
        <div className="flex flex-col gap-4">
          {/* Overall score */}
          <div className={cn(CARD_CLS, 'p-4 flex flex-wrap items-center justify-between gap-4')}>
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{data.companyName}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Financial Health Scorecard — {fmtDate(data.fromDate)} to {fmtDate(data.toDate)}
                {' · '}Generated {fmtDate(data.generatedAt)}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Overall Score
                </div>
                <div className={cn('text-4xl font-bold tabular-nums leading-tight', scoreTone(data.overallScore))}>
                  {data.overallScore === null ? '—' : data.overallScore.toFixed(0)}
                  {data.overallScore !== null && (
                    <span className="text-base font-medium text-gray-400 dark:text-gray-500"> / 100</span>
                  )}
                </div>
              </div>
              <StatusPill status={data.overallStatus} />
            </div>
          </div>

          {/* Categories */}
          {data.categories.length === 0 ? (
            <EmptyState message="No scorecard categories for this period." />
          ) : (
            data.categories.map((c: ScorecardCategory) => (
              <Section
                key={c.category}
                title={c.category}
                actions={
                  <div className="flex items-center gap-3">
                    <span className={cn('text-sm font-bold tabular-nums', scoreTone(c.score))}>
                      {c.score === null ? '—' : `${c.score.toFixed(0)} / 100`}
                    </span>
                    <StatusPill status={c.status} />
                  </div>
                }
              >
                <ScorecardKpiTable kpis={c.kpis} />
              </Section>
            ))
          )}

          {/* Radar */}
          <Section title="Category Scores" subtitle="Only categories with a computed score are plotted">
            {radarData.length === 0 ? (
              <EmptyState message="No category could be scored from the available data." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid className={GRID_CLS} />
                  <PolarAngleAxis dataKey="category" tick={AXIS_TICK} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={AXIS_TICK} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke={CHART_COLOURS[0]}
                    fill={CHART_COLOURS[0]}
                    fillOpacity={0.35}
                  />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* Net profit trend */}
          <Section title="Net Profit Trend">
            {data.trend.length === 0 ? (
              <EmptyState message="No trend data for this period." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.trend} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" className={GRID_CLS} />
                  <XAxis dataKey="period" tick={AXIS_TICK} />
                  <YAxis tick={AXIS_TICK} tickFormatter={axisAmt} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="netProfit"
                    name="Net Profit"
                    stroke={CHART_COLOURS[1]}
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Section>

          {/* Metrics the backend declined to compute */}
          <UnavailableMetrics metrics={data.unavailableMetrics} />
        </div>
      )}

      {/* Print preview */}
      {data && (
        <ReportPrintPreview
          isOpen={showPrint}
          onClose={() => setShowPrint(false)}
          title="Financial Health Scorecard"
          orientation="portrait"
        >
          <ReportHeader
            reportTitle="Financial Health Scorecard"
            printedBy={user?.fullName}
            printedAt={new Date()}
            meta={[
              { label: 'Company', value: data.companyName },
              { label: 'Period', value: `${fmtDate(data.fromDate)} to ${fmtDate(data.toDate)}` },
              {
                label: 'Overall Score',
                value: data.overallScore === null
                  ? 'Unavailable'
                  : `${data.overallScore.toFixed(0)} / 100 (${data.overallStatus})`,
              },
            ]}
          />

          {data.categories.map((c: ScorecardCategory) => (
            <div key={c.category} style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, margin: '8px 0 4px' }}>
                {c.category} — {c.score === null ? 'Unavailable' : `${c.score.toFixed(0)} / 100`} ({c.status})
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                <thead>
                  <tr>
                    {['KPI', 'Value', 'Status', 'Interpretation'].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          background: '#1e3a5f', color: 'white', padding: '5px 6px',
                          textAlign: i === 1 ? 'right' : 'left', fontWeight: 700, fontSize: '9px',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {c.kpis.map(k => {
                    const unavailable = k.value === null || k.status === 'NO_DATA';
                    return (
                      <tr key={k.key} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '3px 6px', color: unavailable ? '#9ca3af' : '#111827' }}>{k.label}</td>
                        <td style={{
                          padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace',
                          color: unavailable ? '#9ca3af' : '#111827',
                        }}>
                          {unavailable ? '—' : fmtByFormat(k.value, k.format)}
                        </td>
                        <td style={{ padding: '3px 6px', color: '#6b7280' }}>{k.status}</td>
                        <td style={{ padding: '3px 6px', color: '#6b7280' }}>{k.interpretation}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

          {data.unavailableMetrics.length > 0 && (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, margin: '14px 0 4px' }}>Not shown on this report</div>
              <ul style={{ paddingLeft: '16px', margin: 0 }}>
                {data.unavailableMetrics.map(m => (
                  <li key={m} style={{ fontSize: '9px', color: '#6b7280' }}>{m}</li>
                ))}
              </ul>
            </>
          )}

          <ReportFooter />
        </ReportPrintPreview>
      )}
    </div>
  );
};
